/* import __COLOCATED_TEMPLATE__ from './generic-filter.hbs.js'; */
/**
 ## Overview
 `GenericFilterComponent` is a dropdown filter that allows selecting
 one or more values.

 This is a strictly a data-down/actions-up component. Binding to the
 `value` property will not work. Instead, listen for changes to the selected
 values using the `onChange` action. Then store that value on the property
 passed to `value` and the filter will stay in sync.

 ```example
 @title Multiple Selection

 @controller-properties
 {
   values: [{
     displayName: 'Location D',
     token: 'location-d'
   }, {
     displayName: 'Location A',
     token: 'location-a'
   }, {
     displayName: 'Location C',
     token: 'location-c'
   }, {
     displayName: 'Location B',
     token: 'location-b'
   }],

   selectedValues: Ember.computed(function() {
     return [this.get('values.firstObject')];
   }),

   selectedValueNicknames: Ember.computed('selectedValues', function() {
     return this.get('selectedValues').mapBy('displayName').join(', ');
   }),

   actions: {
     onFilterChange(value) {
       this.set('selectedValues', value);
     }
   }
 }

 @hbs
 {{generic-filter multiple=true
                           values=values
                           selectedValues=selectedValues
                           filterName=(t-i18n "subunit.subunits")
                           onChange=(action "onFilterChange")}}
 <p>Value: {{selectedValueNicknames}}</p>
 ```

 ```example
 @title Single Selection

 @controller-properties
 {
   values: [{
     displayName: 'Location D',
     token: 'location-d'
   }, {
     displayName: 'Location A',
     token: 'location-a'
   }, {
     displayName: 'Location C',
     token: 'location-c'
   }, {
     displayName: 'Location B',
     token: 'location-b'
   }],

   selectedValue: Ember.computed.reads('values.firstObject'),
   selectedValueIncludingMaster: Ember.computed.reads('values.firstObject'),
   selectedValuesAllowingAll: Ember.computed.reads('values.firstObject'),

   selectedValuesAllowingAllNicknames: Ember.computed('selectedValuesAllowingAll', function() {
     return Ember.makeArray(this.get('selectedValuesAllowingAll')).mapBy('displayName').join(', ');
   }),

   actions: {
     onFilterChange(value) {
       this.set('selectedValue', Ember.get(value, 'firstObject'));
     },

     onFilterIncludingMasterChange(value) {
       this.set('selectedValueIncludingMaster', Ember.get(value, 'firstObject'));
     },

     onFilterAllowingAllChange(value) {
       this.set('selectedValuesAllowingAll', value);
     }
   }
 }

 @hbs
 <div class="grid-row">
 <div class="grid-col grid-col-12-24">
 <strong>Basic</strong>
 {{generic-filter values=values
                           selectedValues=selectedValue
                           filterName=(t-i18n "subunit.subunits")
                           onChange=(action "onFilterChange")}}
 <p>Value: {{selectedValue.displayName}}</p>
 </div>

 <div class="grid-col grid-col-12-24">
 <strong>Allow All: true</strong>
 {{generic-filter allowAll=true
                           values=values
                           selectedValues=selectedValuesAllowingAll
                           filterName=(t-i18n "subunit.subunits")
                           onChange=(action "onFilterAllowingAllChange")}}
 <p>Value: {{selectedValuesAllowingAllNicknames}}</p>
 </div>
 </div>
 ```

 ### Parameters
 @param {Boolean} [allowAll=false] The `allowAll` property only applies when `multiple` is `false`.
 @param {Boolean} [hideIfOneValue=true] Set to `false` to show the filter when there is only one value.
 @param {Boolean} [multiple=false] Set to `true` to allow merchants to select multiple values.
 @param {Array} [values=[]] Array of the value objects.
 @param {String} [values[].displayName] Label to show in the dropdown.
 @param {Boolean} [values[].disabled] Whether the checkbox option is disabled, only applies when `multiple` is `true`.
 @param {Object|Array} [selectedValues=null] Can either be a single value or an array of values.
 @param {String} [filterName={{t-i18n "someTranslation"}}] Defines the  translation for filter type.
 @param {Action} [onChange="change"] Sets the action sent when selectedValues are changed.
 @param {Action} [onOpen=null] Action to call when the filter is opened.
 @param {Action} [onClose=null] Action to call when the filter is closed.
 @param {Boolean} [isSearchable=false] Whether we show search bar to filter the values
 @param {Boolean} [hideSelectAllCheckbox=false] Whether the Select All checkbox is shown, only applies when `multiple` is `true`
 @param {Boolean} [disableSort=false] Whether to not use the default sorting
 @param {Boolean} [isDisabled=false] Whether interaction with the filter should be disabled.
 */

/* eslint square/no-restricted-files:"off" -- unscoped components in this folder are discouraged */
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { computed, set } from '@ember/object';
import { makeArray } from '@ember/array';
import { typeOf } from '@ember/utils';
import { filterValuesLabel } from '@dashboard/shared-ui/utils/filter-label-helper';
const ALL_VALUE = 'allValues';
function copyAsArray(value) {
  // unwrap array proxies
  let arr;
  if (value && typeOf(value.toArray) === 'function') {
    arr = value.toArray();
  } else {
    arr = value;
  }
  return [...makeArray(arr)];
}

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  i18n: service(),
  classNames: ['generic-filter'],
  showFilter: computed('hideIfOneValue', 'displayValues.length', function () {
    // Show the component regardless of the number of units the multiunit merchant has if flag is disabled.
    if (!this.hideIfOneValue) {
      return true;
    }

    // Show the component only if the multiunit merchant has more than one selection and the flag is enabled.
    return this.displayValues?.length > 1;
  }),
  // passed in
  allowAll: false,
  disableSort: false,
  hideIfOneValue: true,
  multiple: false,
  values: null,
  triggerClass: 'button--pill caret-down',
  selectedValues: null,
  filterName: null,
  isSearchable: false,
  hideSelectAllCheckbox: false,
  onOpen: null,
  onClose: null,
  isDisabled: false,
  alignRight: false,
  allValue: ALL_VALUE,
  label: computed('_label', 'allowAll', 'filterName', 'i18n', 'multiple', 'selectedValues.[]', 'values.length', {
    get() {
      if (this._label !== undefined) {
        return this._label;
      }
      const selectedValues = copyAsArray(this.selectedValues);
      const filterName = this.filterName || '';
      if (this.multiple) {
        return filterValuesLabel(selectedValues.mapBy('displayName'), this.values?.length, filterName);
      }
      if (this.allowAll && selectedValues.length === this.values?.length) {
        return this.i18n.t('filter.allValues', {
          filterName
        });
      }
      return selectedValues?.firstObject?.displayName;
    },
    set(_, value) {
      return set(this, '_label', value);
    }
  }),
  singleValue: computed('_singleValue', 'selectedValues.length', 'values.[]', {
    get() {
      if (this._singleValue !== undefined) {
        return this._singleValue;
      }
      const selectedValues = copyAsArray(this.selectedValues);
      if (selectedValues.length === this.values?.length) {
        return ALL_VALUE;
      }
      return selectedValues[0];
    },
    set(_, value) {
      return set(this, '_singleValue', value);
    }
  }),
  displayValues: computed('disableSort', 'multiple', 'selectedValues', 'values.@each.displayName', function () {
    let sortedValues;
    if (this.disableSort) {
      sortedValues = this.values;
    } else {
      sortedValues = [...this.values].sort((a, b) => {
        if (a.nonStandard !== b.nonStandard) {
          return a.nonStandard ? -1 : 1;
        } else if (a.inactive !== b.inactive) {
          return a.inactive ? 1 : -1;
        }
        return a.displayName.localeCompare(b.displayName);
      });
    }
    if (this.multiple) {
      // We need to convert these objects to wrapper objects with an checked property for filter-collection to work
      const selectedValues = copyAsArray(this.selectedValues);
      return sortedValues.map(value => ({
        value,
        checked: selectedValues.includes(value)
      }));
    }
    // Else we use sq-option-set, no conversion needed
    return copyAsArray(sortedValues);
  }),
  // Used as the all values options at the top, when multiple values are selected
  selectAllCheckboxLabel: computed('filterName', 'i18n', function () {
    const filterName = this.filterName || '';
    return this.i18n.t('filter.valueList.all', {
      filterName
    });
  }),
  searchPlaceholderText: computed('filterName', 'i18n', function () {
    const filterName = this.filterName || '';
    return this.i18n.t('filter.placeholderText', {
      filterName
    });
  }),
  actions: {
    // multiple=false action
    singleValueChange(value) {
      const valueCollection = value === ALL_VALUE ? this.values : [value];
      this.onChange(valueCollection);
    },
    // multiple=true action
    multipleValuesChange() {
      const selectedValues = this.displayValues.filterBy('checked').mapBy('value');
      // Converting the selected values back to the actual values that were passed in
      const paramValues = this.values.filter(value => {
        return selectedValues.includes(value);
      });
      this.onChange(copyAsArray(paramValues));
    },
    onOpenFilter() {
      if (this.onOpen) {
        this.onOpen();
      }
    },
    onCloseFilter() {
      if (this.onClose) {
        this.onClose();
      }
    }
  }
});